// File: frontend/src/pages/Socrates/components/AIAgentsAdmin/AdminDashboard/index.js

import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChartLine, faDatabase, faCog, 
  faExclamationTriangle, faCheckCircle 
} from '@fortawesome/free-solid-svg-icons';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import SocratesCard from '../../common/SocratesCard';
import styles from './AdminDashboard.module.css';

const AdminDashboard = ({ agent }) => {
  const { t } = useLanguage();

  // Mock admin stats
  const adminStats = {
    totalRequests: 1247,
    errorRate: 2.3,
    avgResponseTime: 145,
    uptime: 99.7,
    lastDeployment: '2024-01-20T14:30:00Z',
    health: 'healthy'
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <div className={styles.adminDashboard}>
      <div className={styles.header}>
        <h2 className={styles.title}>{t('admin_dashboard')}</h2>
        <p className={styles.subtitle}>
          {t('admin_dashboard_for', { agentName: agent.display_name })}
        </p>
      </div>

      {/* Overview Stats */}
      <div className={styles.statsGrid}>
        <SocratesCard className={styles.statCard}>
          <div className={styles.statHeader}>
            <FontAwesomeIcon icon={faChartLine} className={styles.statIcon} />
            <span className={styles.statLabel}>{t('total_requests')}</span>
          </div>
          <div className={styles.statValue}>{adminStats.totalRequests.toLocaleString()}</div>
          <div className={styles.statChange}>+12% from last week</div>
        </SocratesCard>

        <SocratesCard className={styles.statCard}>
          <div className={styles.statHeader}>
            <FontAwesomeIcon icon={faExclamationTriangle} className={styles.statIcon} />
            <span className={styles.statLabel}>{t('error_rate')}</span>
          </div>
          <div className={styles.statValue}>{adminStats.errorRate}%</div>
          <div className={styles.statChange}>-0.5% from last week</div>
        </SocratesCard>

        <SocratesCard className={styles.statCard}>
          <div className={styles.statHeader}>
            <FontAwesomeIcon icon={faCog} className={styles.statIcon} />
            <span className={styles.statLabel}>{t('avg_response_time')}</span>
          </div>
          <div className={styles.statValue}>{adminStats.avgResponseTime}ms</div>
          <div className={styles.statChange}>-8ms from last week</div>
        </SocratesCard>

        <SocratesCard className={styles.statCard}>
          <div className={styles.statHeader}>
            <FontAwesomeIcon icon={faCheckCircle} className={styles.statIcon} />
            <span className={styles.statLabel}>{t('uptime')}</span>
          </div>
          <div className={styles.statValue}>{adminStats.uptime}%</div>
          <div className={styles.statChange}>+0.1% from last week</div>
        </SocratesCard>
      </div>

      {/* Agent Health */}
      <div className={styles.contentGrid}>
        <SocratesCard className={styles.healthCard}>
          <div className={styles.cardHeader}>
            <h3 className={styles.cardTitle}>
              <FontAwesomeIcon icon={faCheckCircle} className={styles.cardIcon} />
              {t('agent_health')}
            </h3>
          </div>
          <div className={styles.cardContent}>
            <div className={`${styles.healthStatus} ${styles[adminStats.health]}`}>
              <FontAwesomeIcon icon={faCheckCircle} />
              <span>{t('status_healthy')}</span>
            </div>
            <div className={styles.healthDetails}>
              <div className={styles.healthItem}>
                <span className={styles.healthLabel}>{t('last_deployment')}:</span>
                <span className={styles.healthValue}>{formatDate(adminStats.lastDeployment)}</span>
              </div>
              <div className={styles.healthItem}>
                <span className={styles.healthLabel}>{t('version')}:</span>
                <span className={styles.healthValue}>v1.2.3</span>
              </div>
              <div className={styles.healthItem}>
                <span className={styles.healthLabel}>{t('environment')}:</span>
                <span className={styles.healthValue}>Production</span>
              </div>
            </div>
          </div>
        </SocratesCard>

        <SocratesCard className={styles.resourcesCard}>
          <div className={styles.cardHeader}>
            <h3 className={styles.cardTitle}>
              <FontAwesomeIcon icon={faDatabase} className={styles.cardIcon} />
              {t('resources')}
            </h3>
          </div>
          <div className={styles.cardContent}>
            <div className={styles.resourceItem}>
              <span className={styles.resourceLabel}>{t('artifacts')}:</span>
              <span className={styles.resourceValue}>{agent.artifacts_count}</span>
            </div>
            <div className={styles.resourceItem}>
              <span className={styles.resourceLabel}>{t('variables')}:</span>
              <span className={styles.resourceValue}>{agent.variables_count}</span>
            </div>
            <div className={styles.resourceItem}>
              <span className={styles.resourceLabel}>{t('permissions')}:</span>
              <span className={styles.resourceValue}>{agent.permissions?.length || 0}</span>
            </div>
          </div>
        </SocratesCard>
      </div>

      {/* Quick Actions */}
      <SocratesCard className={styles.actionsCard}>
        <div className={styles.cardHeader}>
          <h3 className={styles.cardTitle}>{t('quick_actions')}</h3>
        </div>
        <div className={styles.cardContent}>
          <div className={styles.actions}>
            <button className={styles.actionButton}>
              {t('restart_agent')}
            </button>
            <button className={styles.actionButton}>
              {t('view_logs')}
            </button>
            <button className={styles.actionButton}>
              {t('export_config')}
            </button>
            <button className={styles.actionButton}>
              {t('backup_agent')}
            </button>
          </div>
        </div>
      </SocratesCard>
    </div>
  );
};

export default AdminDashboard;